import React, { useState, useCallback } from 'react';
import { GitBranch, LayoutList, LayoutGrid } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import PipelineColumn from '../../../components/admin/crm/PipelineColumn';
import PipelineCardCompact from '../../../components/admin/crm/PipelineCardCompact';
import PipelineCardDetailed from '../../../components/admin/crm/PipelineCardDetailed';
import LiveSyncBadge from '../../../components/admin/LiveSyncBadge';
import useSmartPolling from '../../../hooks/useSmartPolling';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { 
    id: 'prospecting', 
    label: 'Prospecting', 
    icon: GitBranch, 
    color: '#6366F1' 
  },
  { 
    id: 'qualification', 
    label: 'Qualification', 
    icon: GitBranch, 
    color: '#8B5CF6' 
  },
  { 
    id: 'proposal', 
    label: 'Proposal', 
    icon: GitBranch, 
    color: '#00B4D8' 
  },
  { 
    id: 'negotiation', 
    label: 'Negotiation', 
    icon: GitBranch, 
    color: '#F59E0B' 
  },
  { 
    id: 'closed_won', 
    label: 'Closed Won', 
    icon: GitBranch, 
    color: '#10B981' 
  },
  { 
    id: 'closed_lost', 
    label: 'Closed Lost', 
    icon: GitBranch, 
    color: '#EF4444' 
  }
];

const Pipeline = () => {
  const [stats, setStats] = useState({});
  const [opportunities, setOpportunities] = useState({});
  const [loading, setLoading] = useState({});
  const [compactMode, setCompactMode] = useState(true);
  const [expandedColumns, setExpandedColumns] = useState({
    prospecting: true // First column expanded by default
  });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  const fetchOpportunitiesByStage = useCallback(async (stage, { force = false } = {}) => {
    if (opportunities[stage] && !force) return; // Already loaded

    // FLASH FIX: only show the loading spinner on the FIRST load of a stage.
    // Background refreshes (smart-polling, force=true) keep the cards on
    // screen and swap the data atomically when the response arrives.
    const isFirstLoad = opportunities[stage] === undefined;
    if (isFirstLoad) {
      setLoading(prev => ({ ...prev, [stage]: true }));
    }

    try {

      const response = await fetch(
        `${API_URL}/api/crm/opportunities/by-stage?stage=${stage}&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch opportunities');

      const data = await response.json();

      setOpportunities(prev => ({ ...prev, [stage]: data }));

      // Calculate total value for this stage
      const totalValue = data.reduce((sum, opp) => sum + (opp.value || 0), 0);
      setStats(prev => ({
        ...prev,
        [stage]: {
          ...(prev[stage] || {}),
          count: data.length,
          value: totalValue
        }
      }));
    } catch (error) {
      console.error(`Error fetching opportunities for ${stage}:`, error);
    } finally {
      if (isFirstLoad) {
        setLoading(prev => ({ ...prev, [stage]: false }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchStats = useCallback(async ({ force = false } = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/opportunities/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();

      // Merge stage counts but preserve already-loaded values
      setStats(prev => {
        const next = {};
        STAGES.forEach(stage => {
          next[stage.id] = {
            count: data.stage_counts?.[stage.id] || 0,
            value: prev[stage.id]?.value || 0,
          };
        });
        return next;
      });

      // Refresh every already-loaded stage (keeps cards + values in sync)
      const loadedStages = Object.keys(opportunities).filter(s => opportunities[s]);
      if (!loadedStages.includes('prospecting')) loadedStages.push('prospecting');
      await Promise.all(loadedStages.map(s => fetchOpportunitiesByStage(s, { force: true })));
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (!force) toast({
        title: 'Error',
        description: 'Failed to load pipeline stats',
        variant: 'destructive'
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, opportunities, fetchOpportunitiesByStage]);

  // Smart polling: refresh every 30s when tab visible, pauses when hidden.
  const { lastSync, refreshing, refresh } = useSmartPolling(
    () => fetchStats({ force: true }),
    { intervalMs: 30000, enabled: true }
  );

  const handleColumnExpand = (stage) => {
    setExpandedColumns(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
    
    // Fetch opportunities if not loaded
    if (!opportunities[stage]) {
      fetchOpportunitiesByStage(stage);
    }
  };

  const handleCardClick = (opportunity) => {
    // Future: Open opportunity details modal
    console.log('Opportunity clicked:', opportunity);
  };

  const CardComponent = compactMode ? PipelineCardCompact : PipelineCardDetailed;

  return (
    <div className="h-full flex flex-col">
      <BackButton />

      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <GitBranch className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Sales Pipeline</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-sm text-[#6C757D]">Visual pipeline with opportunity tracking</p>
                <LiveSyncBadge lastSync={lastSync} refreshing={refreshing}
                  onRefresh={refresh} testId="pipeline-live-sync" />
              </div>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setCompactMode(true)}
              variant={compactMode ? 'default' : 'outline'}
              size="sm"
              className={compactMode ? 'bg-[#00B4D8] hover:bg-[#0096B8]' : ''}
            >
              <LayoutGrid className="h-4 w-4 mr-2" />
              Compact
            </Button>
            <Button
              onClick={() => setCompactMode(false)}
              variant={!compactMode ? 'default' : 'outline'}
              size="sm"
              className={!compactMode ? 'bg-[#00B4D8] hover:bg-[#0096B8]' : ''}
            >
              <LayoutList className="h-4 w-4 mr-2" />
              Detailed
            </Button>
          </div>
        </div>
      </div>

      {/* Pipeline Container - Horizontal Scroll */}
      <div 
        className="flex-1 overflow-x-auto overflow-y-hidden pb-4"
        style={{
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth'
        }}
      >
        <div className="flex space-x-4 h-full min-w-max px-1">
          {STAGES.map((stage) => (
            <PipelineColumn
              key={stage.id}
              stage={stage.id}
              title={stage.label}
              icon={stage.icon}
              color={stage.color}
              count={stats[stage.id]?.count || 0}
              value={stats[stage.id]?.value || 0}
              defaultExpanded={expandedColumns[stage.id]}
              onExpand={handleColumnExpand}
              loading={loading[stage.id]}
            >
              {opportunities[stage.id] && opportunities[stage.id].length > 0 ? (
                opportunities[stage.id].map((opp) => (
                  <CardComponent
                    key={opp.id}
                    opportunity={opp}
                    stageColor={stage.color}
                    onClick={handleCardClick}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-[#6C757D] text-sm">
                  No opportunities in this stage
                </div>
              )}
            </PipelineColumn>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs text-[#6C757D] text-center flex-shrink-0">
        Click column headers to expand/collapse • Scroll horizontally to view all stages
      </div>
    </div>
  );
};

export default Pipeline;
